import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { 
  Shield, Lock, Smartphone, Key, Copy, CheckCircle, AlertTriangle, 
  Loader2, RefreshCw, Eye, EyeOff, QrCode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function MFASettings() {
  const { isDarkMode } = useTheme();
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showSecret, setShowSecret] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const response = await api.get('/auth/mfa/status');
      setMfaStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch MFA status');
    } finally {
      setLoading(false);
    }
  };

  const initiateSetup = async () => {
    setSubmitting(true);
    try {
      const response = await api.post('/auth/mfa/setup');
      setSetupData(response.data);
      setBackupCodes(response.data.backup_codes);
      setShowSetupDialog(true);
    } catch (error) {
      toast.error('Failed to initiate MFA setup');
    } finally {
      setSubmitting(false);
    }
  };

  const verifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/mfa/verify-setup', { code: verificationCode });
      toast.success('MFA enabled successfully!');
      setShowSetupDialog(false);
      setSetupData(null);
      setVerificationCode('');
      fetchMFAStatus();
    } catch (error) {
      toast.error('Invalid verification code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const disableMFA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter your current MFA code');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/mfa/disable', { code: disableCode });
      toast.success('MFA disabled successfully');
      setShowDisableDialog(false);
      setDisableCode('');
      fetchMFAStatus();
    } catch (error) {
      toast.error('Invalid MFA code');
    } finally {
      setSubmitting(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter your current MFA code');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/auth/mfa/regenerate-backup-codes', { code: verificationCode });
      setBackupCodes(response.data.backup_codes);
      toast.success('Backup codes regenerated!');
      setVerificationCode('');
    } catch (error) {
      toast.error('Invalid MFA code');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const copyAllBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('All backup codes copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
      </div>
    );
  }

  return (
    <div data-testid="mfa-settings-page" className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className={`font-bold text-2xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Multi-Factor Authentication
        </h1>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
          Add an extra layer of security to your account
        </p>
      </div>

      {/* Status Card */}
      <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            mfaStatus?.mfa_enabled 
              ? (isDarkMode ? 'bg-emerald-600/20 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200')
              : (isDarkMode ? 'bg-amber-600/20 border border-amber-500/30' : 'bg-amber-100 border border-amber-200')
          }`}>
            {mfaStatus?.mfa_enabled ? (
              <Shield className={`w-6 h-6 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            ) : (
              <AlertTriangle className={`w-6 h-6 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {mfaStatus?.mfa_enabled ? 'MFA Enabled' : 'MFA Not Enabled'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {mfaStatus?.mfa_enabled 
                    ? `Enabled on ${new Date(mfaStatus.mfa_enabled_at).toLocaleDateString()}`
                    : 'Your account is not protected with MFA'}
                </p>
              </div>
              {!mfaStatus?.mfa_enabled ? (
                <Button
                  onClick={initiateSetup}
                  disabled={submitting}
                  data-testid="enable-mfa-btn"
                  className="bg-indigo-600 hover:bg-indigo-500 text-white"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Smartphone className="w-4 h-4 mr-2" />}
                  Enable MFA
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowDisableDialog(true)}
                  data-testid="disable-mfa-btn"
                  className={isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}
                >
                  Disable MFA
                </Button>
              )}
            </div>
          </div>
        </div>

        {mfaStatus?.mfa_enabled && (
          <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Backup Codes</p>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  {mfaStatus.backup_codes_remaining} codes remaining
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBackupCodesDialog(true)}
                data-testid="manage-backup-codes-btn"
                className={isDarkMode ? 'border-white/10' : 'border-slate-300'}
              >
                <Key className="w-4 h-4 mr-2" />
                Manage Codes
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>How MFA Works</h3>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
              <span className={`text-sm font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>1</span>
            </div>
            <div>
              <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Download an authenticator app</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Google Authenticator, Authy, or Microsoft Authenticator</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
              <span className={`text-sm font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>2</span>
            </div>
            <div>
              <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Scan the QR code</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Use your authenticator app to scan the code we provide</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
              <span className={`text-sm font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>3</span>
            </div>
            <div>
              <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Enter verification code</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Enter the 6-digit code from your app to complete setup</p>
            </div>
          </div>
        </div>
      </div>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className={`max-w-md ${isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-slate-900'}>Set Up MFA</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>
              Scan the QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          
          {setupData && (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white' : 'bg-slate-100'}`}>
                  <img 
                    src={setupData.qr_code} 
                    alt="MFA QR Code" 
                    className="w-48 h-48"
                    data-testid="mfa-qr-code"
                  />
                </div>
              </div>

              {/* Manual Entry Secret */}
              <div>
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Can't scan? Enter this code manually:
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className={`flex-1 text-xs p-2 rounded font-mono ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-slate-100 text-slate-700'}`}>
                    {showSecret ? setupData.secret : '••••••••••••••••'}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(setupData.secret)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Verification Code Input */}
              <div>
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Enter the 6-digit code from your app:
                </Label>
                <Input
                  data-testid="mfa-verification-input"
                  type="text"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className={`mt-1 text-center text-2xl font-mono tracking-widest ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                />
              </div>

              {/* Backup Codes Notice */}
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-amber-600/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Save your backup codes! You'll need them if you lose access to your authenticator.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>Cancel</Button>
            <Button
              onClick={verifySetup}
              disabled={submitting || verificationCode.length !== 6}
              data-testid="verify-mfa-btn"
              className="bg-indigo-600 hover:bg-indigo-500 text-white"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Verify & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className={`max-w-sm ${isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-slate-900'}>Disable MFA</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>
              Enter your current MFA code to disable two-factor authentication
            </DialogDescription>
          </DialogHeader>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-red-600/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              This will make your account less secure. Are you sure?
            </p>
          </div>

          <div>
            <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
              Current MFA Code:
            </Label>
            <Input
              type="text"
              maxLength={6}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className={`mt-1 text-center text-2xl font-mono tracking-widest ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300'}`}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={disableMFA}
              disabled={submitting || disableCode.length !== 6}
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Disable MFA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent className={`max-w-md ${isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-slate-900'}>Backup Codes</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>
              Use these codes if you lose access to your authenticator app
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {backupCodes.length > 0 ? (
              <>
                <div className={`grid grid-cols-2 gap-2 p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  {backupCodes.map((code, idx) => (
                    <code 
                      key={idx} 
                      className={`text-center py-2 rounded font-mono text-sm ${isDarkMode ? 'bg-zinc-900 text-zinc-300' : 'bg-white text-slate-700'}`}
                    >
                      {code}
                    </code>
                  ))}
                </div>
                <Button
                  variant="outline"
                  onClick={copyAllBackupCodes}
                  className="w-full"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Codes
                </Button>
              </>
            ) : (
              <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Enter your MFA code to view or regenerate backup codes</p>
              </div>
            )}

            {/* Regenerate Section */}
            <div className={`pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
              <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                Enter current MFA code to regenerate:
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className={`text-center font-mono ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                />
                <Button
                  onClick={regenerateBackupCodes}
                  disabled={submitting || verificationCode.length !== 6}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowBackupCodesDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
